import {
  isDeletedField,
  isPagelinkField,
  isSystemField,
} from '../business-logic/fields'
import type { ComponentRef } from '@wix/editor-platform-sdk-types'
import type { Schema } from '@wix/wix-data-schema-types'
import type { AppApi, ControllerBinding } from '../appApiTypes'
import type {
  BindingConfig,
  FieldBindingConfig,
} from '@wix/wix-data-client-types'

export type FieldPath = string
export type FieldMap = Record<FieldPath, FieldPath>
export type FieldBindingDetail = {
  fieldPath: FieldPath
  fieldBinding: FieldBindingConfig
  controllerBinding: ControllerBinding
}

const isFieldBinding = (
  binding: BindingConfig,
): binding is FieldBindingConfig =>
  binding.type === 'Field' && 'fieldPath' in binding && !!binding.fieldPath

const getFieldMap = ({
  fieldBindingDetail,
  sourceSchema,
  destinationSchema,
}: {
  fieldBindingDetail: FieldBindingDetail[]
  sourceSchema: Schema
  destinationSchema: Schema
}): FieldMap => {
  const fieldMap: FieldMap = {}
  const usedFieldNames: FieldPath[] = []

  const findFieldBinding = (
    searchFieldPath: FieldPath,
  ): FieldBindingDetail | undefined =>
    fieldBindingDetail.find(({ fieldPath }) => searchFieldPath === fieldPath)

  const createMatch = (
    sourceFieldPath: FieldPath,
    destinationFieldPath: FieldPath,
  ) => {
    fieldMap[sourceFieldPath] = destinationFieldPath
    usedFieldNames.push(destinationFieldPath)
  }

  const pairPrimaryFields = () => {
    const sourceDisplayField = sourceSchema.displayField
    const destinationDisplayField = destinationSchema.displayField

    if (
      sourceDisplayField &&
      destinationDisplayField &&
      findFieldBinding(sourceDisplayField) &&
      !fieldMap[sourceDisplayField] &&
      !usedFieldNames.includes(destinationDisplayField)
    ) {
      createMatch(sourceDisplayField, destinationDisplayField)
    }
  }

  const pairMatchingFieldNames = () => {
    fieldBindingDetail.forEach(({ fieldPath }) => {
      if (
        destinationSchema.fields[fieldPath] &&
        sourceSchema.fields[fieldPath].type ===
          destinationSchema.fields[fieldPath].type
      ) {
        createMatch(fieldPath, fieldPath)
      }
    })
  }

  const pairToFirstAvailableFieldByType = () => {
    fieldBindingDetail
      .filter(
        ({ fieldPath }) =>
          !fieldMap[fieldPath] && sourceSchema.fields[fieldPath],
      )
      .forEach(({ fieldPath }) => {
        const availableField = Object.entries(destinationSchema.fields).find(
          ([fieldName, field]) =>
            field.type === sourceSchema.fields[fieldPath].type &&
            !usedFieldNames.includes(fieldName) &&
            !isSystemField(field) &&
            !isDeletedField(field),
        )

        if (availableField) {
          const [availableFieldName] = availableField
          createMatch(fieldPath, availableFieldName)
        }
      })
  }

  pairPrimaryFields()
  pairMatchingFieldNames()
  pairToFirstAvailableFieldByType()

  return fieldMap
}

const getFieldBindingDetail = (controllerBindings: ControllerBinding[]) =>
  controllerBindings
    .filter(({ bindings }) => bindings?.length > 0)
    .flatMap(controllerBinding =>
      controllerBinding.bindings.filter(isFieldBinding).map(fieldBinding => ({
        fieldPath: fieldBinding.fieldPath,
        fieldBinding,
        controllerBinding,
      })),
    )

export const getCollectionsFieldMap = async ({
  appApi,
  controllerRef,
  sourceSchema,
  destinationSchema,
  pageLinkFieldKey,
}: {
  appApi: AppApi
  controllerRef: ComponentRef
  sourceSchema: Schema
  destinationSchema: Schema
  pageLinkFieldKey?: string
}) => {
  const controllerBindings = await appApi.fetchControllerBindings({
    controllerRef,
  })

  const fieldBindingDetail = getFieldBindingDetail(controllerBindings)

  const fieldMap = getFieldMap({
    fieldBindingDetail,
    sourceSchema,
    destinationSchema,
  })

  const pageLinkField =
    pageLinkFieldKey &&
    fieldBindingDetail.find(({ fieldPath }) =>
      isPagelinkField(sourceSchema.fields[fieldPath]),
    )

  if (pageLinkField) {
    fieldMap[pageLinkField.fieldPath] = pageLinkFieldKey
  }

  return fieldMap
}
